import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog } from '../dialog/dialog.jsx';
import { Pill } from '../display/pill.jsx';
import { usePickerList } from '../../hooks/use-picker-list.js';
import { splitTagNames, joinTagNames } from './../../../../shared-site-web/tag-utils.js';

/**
 * @param {Object} props
 * @param {string} [props.id]
 * @param {string} [props.value] - comma-joined tag names
 * @param {string[]} [props.options] - all available tag names
 * @param {(value: string) => void} props.onChange
 * @param {(name: string) => Promise<{ id: string, name: string }>} [props.onCreate]
 * @param {string} [props.placeholder]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function TagPicker({ id, value = '', options = [], onChange, onCreate, placeholder, className }) {
	const { t } = useTranslation();
	const [draft, setDraft] = useState('');
	const [open, setOpen] = useState(false);
	const [activePill, setActivePill] = useState(-1);
	const [pendingCreate, setPendingCreate] = useState(/** @type {string | null} */ (null));
	const [creating, setCreating] = useState(false);
	const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
	const baseId = id || 'tag-picker';

	const selected = splitTagNames(value);
	const draftName = draft.trim();
	const query = draftName.toLowerCase();

	const matches = options
		.filter((o) => !selected.includes(o))
		.filter((o) => o.toLowerCase().includes(query));

	const showNewOption =
		Boolean(draftName) && Boolean(onCreate) && !options.some((o) => o.toLowerCase() === query);
	const suggestionCount = matches.length + (showNewOption ? 1 : 0);

	const suggestions = matches.map((name) => ({ type: 'existing', name }));
	if (showNewOption) {
		suggestions.push({ type: 'new', label: t('field.createNewTag', { name: draftName }) });
	}

	function commit(names) {
		const next = [...new Set([...selected, ...names])];
		onChange(joinTagNames(next));
		setDraft('');
		setHighlighted(0);
		setActivePill(-1);
		setOpen(false);
	}

	const { rootRef, listRef, clampHighlight, setHighlighted, showSuggestions, flipUp, alignRight, handleKeyDown: pickerKeyDown } =
		usePickerList({
			idPrefix: baseId, open, setOpen, suggestionCount,
			onEnter(i) {
				if (i >= matches.length) {
					if (draftName && onCreate) { setPendingCreate(draftName); }
					return;
				}
				const highlightedOption = matches[i];
				if (highlightedOption) { commit([highlightedOption]); return; }
				const exact = matches.find((o) => o.toLowerCase() === query);
				if (exact) { commit([exact]); return; }
				if (draftName && onCreate) { setPendingCreate(draftName); }
			},
		});

	function removeAt(index) {
		onChange(joinTagNames(selected.filter((_, idx) => idx !== index)));
		setActivePill(-1);
	}

	/**
	 * Move the active pill selection with left/right arrows.
	 * @param {'ArrowLeft' | 'ArrowRight'} key
	 */
	function movePillSelection(key) {
		if (activePill === -1) {
			if (key === 'ArrowLeft' && selected.length > 0) { setActivePill(selected.length - 1); }
			return;
		}
		if (key === 'ArrowLeft') {
			setActivePill(Math.max(0, activePill - 1));
		} else {
			setActivePill(activePill === selected.length - 1 ? -1 : activePill + 1);
		}
	}

	/**
	 * Handle keys while a pill is active (for deletion).
	 * @param {import('react').KeyboardEvent<HTMLInputElement>} e
	 * @returns {boolean} whether the key was handled
	 */
	function handleActivePillKey(e) {
		if (e.key === 'Backspace' || e.key === 'Delete') {
			e.preventDefault();
			removeAt(activePill);
			return true;
		}
		if (e.key === 'Enter' || e.key === 'Escape') {
			e.preventDefault();
			setActivePill(-1);
			return true;
		}
		if (e.key.length === 1) { setActivePill(-1); }
		return false;
	}

	/**
	 * @param {import('react').KeyboardEvent<HTMLInputElement>} e
	 */
	function handleKeyDown(e) {
		if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && draft === '') {
			e.preventDefault();
			movePillSelection(e.key);
			return;
		}
		if (activePill !== -1 && handleActivePillKey(e)) { return; }
		if (pickerKeyDown(e)) { return; }
		if (e.key === 'Backspace' && draft === '' && selected.length > 0 && activePill === -1) {
			removeAt(selected.length - 1);
		}
	}

	async function confirmCreate() {
		if (!pendingCreate || !onCreate) { return; }
		setCreating(true);
		try {
			const tag = await onCreate(pendingCreate);
			commit([tag.name]);
		} finally { setCreating(false); setPendingCreate(null); inputRef.current?.focus(); }
	}

	return (
		<div ref={rootRef} className={`tag-picker${className ? ' ' + className : ''}`}>
			<div className="tag-picker-input">
				{selected.map((name, i) => (
					<Pill
						key={name}
						className={i === activePill ? 'is-selected' : undefined}
						onClick={() => setActivePill(i)}
						onRemove={() => removeAt(i)}
						removeLabel={t('aria.removeTag', { name })}
					>
						{name}
					</Pill>
				))}
				<input
					id={id}
					ref={inputRef}
					type="text"
					value={draft}
					role="combobox"
					aria-haspopup="listbox"
					aria-expanded={showSuggestions}
					aria-autocomplete="list"
					aria-controls={showSuggestions ? `${baseId}-listbox` : undefined}
					aria-activedescendant={showSuggestions ? `${baseId}-option-${clampHighlight}` : undefined}
					onChange={(e) => { setDraft(e.target.value); setOpen(true); setHighlighted(0); setActivePill(-1); }}
					onFocus={() => setOpen(true)}
					onPointerDown={() => setOpen(true)}
					onMouseDown={() => setOpen(true)}
					onBlur={() => setOpen(false)}
					onKeyDown={handleKeyDown}
					placeholder={placeholder || t('field.tagPlaceholder')}
				/>
			</div>
			{showSuggestions && (
				<ul
					id={`${baseId}-listbox`}
					ref={listRef}
					className={`picker-suggestions${flipUp ? ' is-flipped' : ''}${alignRight ? ' is-right-aligned' : ''}`}
					role="listbox"
					aria-label={t('aria.pickerOptions')}
					onMouseDown={(e) => e.preventDefault()}
				>
					{suggestions.map((s, i) => (
						<li
							key={s.type === 'new' ? 'new-tag' : s.name}
							id={`${baseId}-option-${i}`}
							role="option"
							tabIndex={-1}
							aria-selected={i === clampHighlight}
							className={`picker-suggestion${s.type === 'new' ? ' is-new' : ''}${i === clampHighlight ? ' is-highlighted' : ''}`}
							onMouseDown={() => {
								if (s.type === 'existing') { commit([s.name]); }
								else if (onCreate) { setPendingCreate(draftName); }
							}}
							onMouseEnter={() => setHighlighted(i)}
						>
							{s.type === 'new' ? s.label : s.name}
						</li>
					))}
				</ul>
			)}
			{pendingCreate && (
				<Dialog
					title={t('dialog.createTagTitle')}
					message={t('dialog.createTagMessage', { name: pendingCreate })}
					confirmLabel={t('btn.createTag')}
					onConfirm={confirmCreate}
					onCancel={() => { setPendingCreate(null); inputRef.current?.focus(); }}
					loading={creating}
				/>
			)}
		</div>
	);
}
